import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FulfillmentTypeItem } from 'src/app/pages/checkout/models/location.model';
import { CartItem, MenuItem, MenuItemData, MenuItemForPayment } from '../models/cart-item.model';
import { FulfillmentType, SubscriptionPhase } from '../models/order.model';
import { Loyalty, Order } from '../models/payment-item.model';
import {
  Country, Coupon,
  Restaurant,
  Taxes
} from '../models/restaurant.model';
import { CartStore } from '../stores/cart.store';
import { PaymentStore } from '../stores/payment.store';
import { RestaurantStore } from '../stores/restaurant.store';
import { FormatDataService } from './format-data.service';
import { ItemValidateService } from './item-validate.service';
import { OrderApiService } from './order.api.service';
import { PaymentService } from './payment.service';
import { UtilService } from './util.service';
import { Item, Modifier, Variation } from '../models/catalog.model';

@Injectable({
  providedIn: 'root',
})
export class ProcessDataService {
  private restaurant: Restaurant;
  private locationId: string;
  private destroyed$ = new Subject<void>();
  private cartProduct: MenuItemForPayment;

  public productQuantity: number = 0;
  public specialInstructions: string = '';
  public modifiers: Modifier[] = [];

  private readonly pickupFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'Pickup',
    'icon-pickup',
    true,
    FulfillmentType.PICKUP, 0
  );
  constructor(
    private restaurantStore: RestaurantStore,
    private cartStore: CartStore,
    private titleService: Title,
    private paymentStore: PaymentStore,
    private orderApiService: OrderApiService,
    private paymentService: PaymentService,
    private itemValidateService: ItemValidateService,
    private formatDataService: FormatDataService,
    public utilService: UtilService
  ) {}

  public setRestaurantData(restaurant: Restaurant): void {
    this.restaurant = restaurant;
    this.locationId = restaurant.locationId;
    this.cartStore.kyooFee = restaurant.kyooFee;
    this.cartStore.autoGratuityPercent = restaurant.autoGratuityPercent;
    this.cartStore.driverGratuityPercent = restaurant.defaultDeliveryAutoGratuityPercent;
    this.cartStore.enableRestaurantTips = restaurant.enableRestaurantTip;
    this.cartStore.tippingEnabled = restaurant.isTippingEnabled;
    this.cartStore.orderConfirmationText = restaurant.orderConfirmationText;
    this.titleService.setTitle(restaurant.name);
    this.restaurantStore.hideRestaurantOrdersAhead = restaurant.hideOrdersAhead;
    this.restaurantStore.disableTodayOrders = restaurant.disableTodayOrders;
    this.restaurantStore.timezone = restaurant.timezone;
    this.restaurantStore.loyaltyPointsName = restaurant.newLoyaltyPointsName;
    this.restaurantStore.restaurantCountryInfo = Country.getCountryByName(restaurant.country);
    this.restaurantStore.estimateDeliveryTime =
      this.itemValidateService.convertLeadTimeToMinutes(restaurant.estimatedPrepTimeDuration) +
      this.itemValidateService.convertLeadTimeToMinutes(restaurant.addlDeliveryPrepTimeDuration);
    if (restaurant.availableStatus) {
      this.restaurantStore.setRestaurantAvailableStatus(restaurant.availableStatus);
      this.restaurantStore.setRestaurantIsAvailable(true);
    } else {
      this.restaurantStore.setRestaurantIsAvailable(false);
    }
    if (restaurant.isLoyaltyEnabled) {
      this.paymentStore.useLoyaltyChecked = true;
      this.cartStore.isLoyaltyEnabled = restaurant.isLoyaltyEnabled;
      this.cartStore.loyalty = new Loyalty([], this.cartStore.loyalty ? this.cartStore.loyalty.pointsValue : 0, true);
    }
    if (restaurant?.enablePromoCodes) {
      this.restaurantStore.checkRouteContainsDiscountOption();
      this.getPromoCodeData();
    }
    if (this.restaurantStore.subscriptionCode && !!this.restaurantStore.subscriptionCode.length) {
      this.getSubscriptionData();
    }
  }

  public getPromoCodeData(): void {
    this.orderApiService
      .getDiscountByPromoCode(this.locationId, this.restaurantStore.discountPromo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((discount: Coupon) => {
        if (discount != null) {
          discount.locationId = this.locationId;
          this.cartStore.discounts = [discount];
        }
      });
  }

  public getSubscriptionData(): void {
    this.orderApiService
      .getSubscriptionData(this.locationId, this.restaurantStore.subscriptionCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (subscriptionData: SubscriptionPhase[]) => {
          if (subscriptionData != null && subscriptionData.length > 0) {
            this.cartStore.subscriptionsData = subscriptionData;
          } else {
            this.restaurantStore.subscriptionCode = null;
          }
        },
        (error) => {
          if (error.error.message.includes('not found')) {
            this.restaurantStore.subscriptionCode = null;
          }
        }
      );
  }

  public validateItemLeadTime(item: Item): boolean {
    const productValidVariations: boolean[] = [];
    for (const variation of item.variations) {
      if (variation.pickup_lead_time) {
        productValidVariations.push(this.itemValidateService.validLeadTime(variation, this.restaurant.hoursOfOperation));
      } else {
        productValidVariations.push(true);
      }
    }

    return productValidVariations.filter((variation) => variation === true).length > 0;
  }

  public validateItemAvailableDays(item: Item): boolean {
    const productValidVariations: boolean[] = [];
    for (const variation of item.variations) {
      if (variation.daily_availability_enabled) {
        productValidVariations.push(this.itemValidateService.validDayRestriction(variation));
      } else {
        productValidVariations.push(true);
      }
    }
    return productValidVariations.filter((variation) => variation === true).length > 0;
  }

  public validateItemAvailableFulfillments(item: Item): boolean {
    const productValidVariations: boolean[] = [];
    for (const variation of item.variations) {
      if (variation.fulfillment_restriction_enabled) {
        productValidVariations.push(this.itemValidateService.validFulfillments(variation, this.restaurantStore.enabledFulfillments));
      } else {
        productValidVariations.push(true);
      }
    }

    return productValidVariations.filter((variation) => variation === true).length > 0;
  }

  public getFilteredVariations(itemVariations: Variation[]): Variation[] {
    const filteredVariationList: Variation[] = [];
    for (const variation of itemVariations) {
      let variationHidden = false;
      // Hide or disable unorderable time-restricted items during service for ASAP orders
      if (this.restaurant?.isAcceptingOrders && !this.restaurant.allowScheduledPickups && !this.validateVariationAvailableTimes(variation)) {
        variationHidden = !this.restaurant.showAllTimeRestrictedItems;
      }
      // Hide or disable items with incorrect fulfillment types
      if (variation.fulfillment_restriction_enabled && !this.itemValidateService.validFulfillments(variation, this.restaurantStore.enabledFulfillments)) {
        variationHidden = variationHidden || !this.restaurant.showAllFulfillmentRestrictedItems;
      }
      if (!variationHidden) {
        filteredVariationList.push(variation);
      }
    }
    return filteredVariationList;
  }

  public validateVariationAvailableTimes(variation: Variation): boolean {
    let isValidLeadTime = true;
    let isValidWeekDay = true;
    if (variation == null) {
      return false;
    }
    if (variation.opening_lead_time) {
      isValidLeadTime = this.itemValidateService.validLeadTime(variation, this.restaurant.hoursOfOperation);
    }
    if (variation.daily_availability_enabled) {
      isValidWeekDay = this.itemValidateService.validDayRestriction(variation);
    }
    return isValidLeadTime && isValidWeekDay;
  }

  // public loadNextVariationsInventory(locationId: string, variationIdList: string[]) {
  //   for (let i = 0; i < variationIdList.length; i += 100) {
  //     this.restaurantStore.getProductsInventory(locationId, variationIdList.slice(i, i + 100)).subscribe();
  //   }
  // }

  public addItemToCart(product: any, variationId: string): any {
    const filteredVariationList = this.getFilteredVariations(product.variations);
    if (filteredVariationList.length === 0) {
      return;
    }
    const orderVariation = filteredVariationList.find((variation) => variation.id === variationId);
    const firstVariationIndex = product.variations.findIndex((variation: Variation) => variation.id === orderVariation.id);
    const firstVariationItem = product.variations[firstVariationIndex];
    const productInfo = product.info;

    const menuItem = new MenuItemData(
      firstVariationItem.id,
      null,
      productInfo.name,
      productInfo.imageUrl,
      this.productQuantity,
      firstVariationItem,
      this.modifiers,
      productInfo.description,
      null,
      this.specialInstructions
    );
    this.cartProduct = new MenuItemForPayment(this.locationId, product.categoryId, product.categoryId, productInfo.id, menuItem);
    this.cartProduct.item.specialInstructions = this.specialInstructions;
    // @ts-ignore
    return [this.validateItem(menuItem), this.cartProduct];
  }

  private validateItem(item: CartItem): boolean {
    const isValidLeadTime = this.validateSingleItemLeadTimeProduct(item);
    const isValidWeekDay = this.validateSingleItemAvailableDays(item);
    const isValidFulfillment = this.validateSingleItemAvailableFulfillments(item);
    const isValidDateTimeRange = this.validateSingleItemDateTimeRange(item);
    const isValidRestaurantFulfillment = true;
    const isValidRestaurantTime = true;
    return isValidLeadTime && isValidWeekDay && isValidFulfillment && isValidDateTimeRange && isValidRestaurantFulfillment && isValidRestaurantTime;
  }

  private validateSingleItemLeadTimeProduct(item: CartItem): boolean {
    if (item == null || item.variation == null) {
      return;
    }
    if (item.variation.opening_lead_time) {
      return this.itemValidateService.validLeadTime(item.variation, this.restaurant.hoursOfOperation);
    } else {
      return true;
    }
  }

  private validateSingleItemAvailableDays(item: CartItem): boolean {
    if (item.variation.daily_availability_enabled) {
      return this.itemValidateService.validDayRestriction(item.variation);
    } else {
      return true;
    }
  }

  private validateSingleItemAvailableFulfillments(item: CartItem): boolean {
    if (item == null || item.variation == null) {
      return false;
    }
    if (item.variation.fulfillment_restriction_enabled) {
      return this.itemValidateService.validFulfillmentRestriction(item.variation, this.pickupFulfillmentType);
    } else {
      return true;
    }
  }

  private validateSingleItemDateTimeRange(item: CartItem): boolean {
    if (item.variation.opening_lead_time) {
      return this.itemValidateService.validItemDatetimeRange(item.variation);
    } else {
      return true;
    }
  }

  public calculateOrder(locationId: string, order: Order): void {
    this.orderApiService.calculateOrder(locationId, order)
      .subscribe((response: any) => {
      this.cartStore.updateOrderMapData(
        locationId,
        order,
        response.total_money.amount,
        response.taxes,
        response.discounts,
        response.accumulated_loyalty_points,
        response.line_items,
        response.estimated_duration,
        response.kyoo_fee_money.amount,
        response.delivery_fee_money.amount,
        response.total_tax_money.amount
      );
      this.paymentStore.isWaitingUpdate = false;
      this.cartStore.updateCartInProgress = false;
    });
  }

  public sendOrder(locationId: string, order: Order, taxes: Taxes[], merchantId?: string): void {
    order.applied_taxes = taxes.map((tax) => tax.id);
    let merchantUrl;
    if (merchantId) {
      merchantUrl = `merchants/${merchantId}`
    }
    this.orderApiService.sendOrder(locationId, order, merchantUrl).subscribe(({ id, total_money, discounts, taxes }: any) => {
      const amount = total_money.amount - this.cartStore.taxesSum(taxes);
      this.cartStore.orderMap[locationId] = {
        id,
        order,
        amount,
        discounts,
        taxes,
        loyaltyPoints: 0,
        isUpdated: false,
        isSubmitted: false,
        isPaid: false,
      };
      this.paymentStore.isWaitingUpdate = false;
    });
  }
}
